import React, { useContext, useState } from 'react'
import { Sidebar } from './sidebar'
import { Checkbox } from 'antd'
import { useCustomFetch } from '../../hooks/useCustomFetch'
import toast from 'react-hot-toast'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AuthContext } from '../../context/AuthContext'
import evermuseLogo from '@/assets/evermuse-logo-21h.svg'

export function CreateWorkspace() {
  const { user, setUser } = useContext(AuthContext)
  const [searchParams] = useSearchParams()
  const [workspaceName, setWorkspaceName] = useState('')
  const [allowMailExtension, setAllowMailExtension] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const customFetch = useCustomFetch()

  if (!user || !user.email) return

  const split = user.email.split('@')
  if (split.length < 2) throw new Error('Error with user extension')
  const mailExtension = split[1]
  const showMailExtension =
    mailExtension !== 'gmail.com' &&
    mailExtension !== 'googlemail.com' &&
    mailExtension !== 'outlook.com' &&
    mailExtension !== 'hotmail.com' &&
    !mailExtension.endsWith('.onmicrosoft.com')

  async function createWorkspace() {
    try {
      if (!workspaceName) return toast.error('Please enter a workspace name')
      setIsLoading(true)
      const response = await customFetch('/createWorkspace', 'POST', {
        name: workspaceName,
        mailExtension: showMailExtension && allowMailExtension ? mailExtension : undefined,
      })
      if (response.error) throw new Error(response.error)
      if (!Object.keys(response.newWorkspace).length) throw new Error('Error getting new workspace')
      setUser((prevUser) => ({
        ...prevUser,
        workspaces: { ...prevUser.workspaces, ...response.newWorkspace },
        active_workspace: Object.keys(response.newWorkspace)[0],
      }))
      toast.success('Workspace created!')
      setIsLoading(false)
      const planId = searchParams.get('plan_id')
      if (planId) navigate(`/dashboard/home-new?plan_id=${planId}`)
      else navigate('/dashboard/home-new')
    } catch (error) {
      console.error('Error creating workspace', error)
      toast.error('Error creating workspace')
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Logo at top */}
      <div className="px-6 pt-8 pb-12">
        <div className="max-w-md mx-auto">
          <img src={evermuseLogo} alt="Evermuse" width="170" height="21" className="mx-auto" />
        </div>
      </div>

      {/* Primary content area - centered vertically */}
      <div className="flex-1 flex items-center px-6 py-8">
        <div className="max-w-md mx-auto w-full">
          <p className="text-2xl font-bold font-['DM_Sans'] text-zinc text-center mb-2">Create a Workspace</p>
          <p className="text-base font-medium font-['DM_Sans'] text-zinc text-center mb-8">
            A workspace is a space for your organization to collaborate on user research projects.
          </p>

          <div className="space-y-6">
            <div>
              <div className="flex items-center mb-2">
                <p className="text-[#40424A] text-xs font-extrabold font-['Manrope'] uppercase leading-[13.20px] tracking-wide">
                  Workspace name
                </p>
                <p className="ml-[5px] text-[#FF4A8E] text-[24px] font-medium font-['DM_Sans'] leading-none">*</p>
              </div>
              <input
                className="w-full px-5 py-2.5 bg-grey-100 rounded shadow-inner border border-grey-200 placeholder-grey-400"
                placeholder="Enter Workspace Name"
                value={workspaceName}
                onChange={(e) => setWorkspaceName(e.target.value)}
              />
            </div>

            {showMailExtension && (
              <div className="flex items-start gap-2">
                <Checkbox
                  checked={allowMailExtension}
                  onChange={(e) => setAllowMailExtension(e.target.checked)}
                  className="mt-1"
                />
                <div className="flex-1 cursor-pointer" onClick={() => setAllowMailExtension(!allowMailExtension)}>
                  <span className="text-black text-base font-normal font-['DM_Sans'] leading-[23px]">
                    Accept anyone with an{' '}
                  </span>
                  <span className="text-black text-base font-bold font-['DM_Sans'] leading-[23px]">
                    @{mailExtension}{' '}
                  </span>
                  <span className="text-black text-base font-normal font-['DM_Sans'] leading-[23px]">address</span>
                </div>
              </div>
            )}

            <button
              className="w-full h-12 px-[30px] py-3.5 bg-[#FF4A8E] rounded justify-center items-center gap-2.5 inline-flex disabled:opacity-60 disabled:cursor-not-allowed"
              onClick={createWorkspace}
              disabled={isLoading}
            >
              <p className="text-center text-white text-lg font-medium font-['DM_Sans'] leading-tight">
                {isLoading ? 'Creating...' : 'Create & Continue'}
              </p>
            </button>
          </div>
        </div>
      </div>

      {/* Large spacing before sidebar */}
      <div className="h-24"></div>

      {/* Sidebar content below */}
      <div className="bg-grey-100 px-6 py-12">
        <Sidebar />
      </div>
    </div>
  )
}
